import * as Tooltip from "@radix-ui/react-tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useCallback, useEffect, useMemo } from "react";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { PageMeta } from "@/meta/pageMeta";
import { axiosClient, queryClient } from "@api/clients.ts";
import { useErrorHandler } from "@api/errorHandler/apiErrorHandler";
import { SkeletonContainer } from "@atoms/loader";
import { ToastPortal } from "@atoms/toastPortal";
import { AppRouter } from "@routes/appRouter";
import { useAxiosErrorStore } from "@stores/axiosErrorStore";
import { ThemeProvider } from "@theme";

import { namespaces } from "../i18n/settings";

import "@/styles/main.css";
import "react-toastify/dist/ReactToastify.css";
import { useElectronSalesSync } from "./electronSalesSync";
import "remixicon/fonts/remixicon.css";

// import "react-pdf/dist/esm/Page/AnnotationLayer.css";
// import "react-pdf/dist/esm/Page/TextLayer.css";
// initSentry();

export function App() {
  useElectronSalesSync();
  const { i18n } = useTranslation(namespaces);

  const isMobile = import.meta.env.VITE_MOBILE === "true";

  const setError = useAxiosErrorStore(useShallow((state) => state.setError));

  const errorHandlerFunc = useCallback(
    (message: string, status: number) => {
      setError({ message, status });
    },
    [setError],
  );

  const errorHandler = useErrorHandler(errorHandlerFunc);

  useEffect(() => {
    const interceptor = axiosClient.interceptors.response.use(
      undefined,
      errorHandler,
    );

    return () => {
      axiosClient.interceptors.response.eject(interceptor);
    };
  }, [errorHandler]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dir = useMemo(() => i18n.dir(i18n.language), [i18n.language]);

  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [dir, i18n.language]);

  useEffect(() => {
    document.body.dataset.mobile = isMobile?.toString();
  }, [isMobile]);

  return (
    <ThemeProvider>
      <HelmetProvider>
        <PageMeta />
        <Tooltip.Provider delayDuration={0}>
          <QueryClientProvider client={queryClient}>
            <SkeletonContainer>
              <AppRouter />
            </SkeletonContainer>
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition={"bottom-left"}
            />
            <ToastPortal rtl={i18n.dir() === "rtl"} />
          </QueryClientProvider>
        </Tooltip.Provider>
      </HelmetProvider>
    </ThemeProvider>
  );
}
